import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import {
  getUploadSas, uploadFileToBlob, createBatch, getBatchStatus,
  ACCEPTED_VIDEO_EXTENSIONS,
} from '../services/biomechanicsService';
import { AZURE } from '../services/api';
import {
  Upload, Trash2, Play, CheckCircle2, AlertTriangle, Loader2,
  Waves, Clock, Eye, X, Plus, Layers,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════

const MAX_VIDEOS = 8;

// ── Individual Events ──
const INDIVIDUAL_EVENTS = [
  { value: '50-freestyle', label: '50m Livre', distance: 50, style: 'freestyle' },
  { value: '100-freestyle', label: '100m Livre', distance: 100, style: 'freestyle' },
  { value: '200-freestyle', label: '200m Livre', distance: 200, style: 'freestyle' },
  { value: '400-freestyle', label: '400m Livre', distance: 400, style: 'freestyle' },
  { value: '800-freestyle', label: '800m Livre', distance: 800, style: 'freestyle' },
  { value: '1500-freestyle', label: '1500m Livre', distance: 1500, style: 'freestyle' },
  { value: '50-backstroke', label: '50m Costas', distance: 50, style: 'backstroke' },
  { value: '100-backstroke', label: '100m Costas', distance: 100, style: 'backstroke' },
  { value: '200-backstroke', label: '200m Costas', distance: 200, style: 'backstroke' },
  { value: '50-breaststroke', label: '50m Peito', distance: 50, style: 'breaststroke' },
  { value: '100-breaststroke', label: '100m Peito', distance: 100, style: 'breaststroke' },
  { value: '200-breaststroke', label: '200m Peito', distance: 200, style: 'breaststroke' },
  { value: '50-butterfly', label: '50m Borboleta', distance: 50, style: 'butterfly' },
  { value: '100-butterfly', label: '100m Borboleta', distance: 100, style: 'butterfly' },
  { value: '200-butterfly', label: '200m Borboleta', distance: 200, style: 'butterfly' },
  { value: '200-medley', label: '200m Medley', distance: 200, style: 'medley' },
  { value: '400-medley', label: '400m Medley', distance: 400, style: 'medley' },
];

// ── Relay Events ──
const RELAY_EVENTS = [
  { value: '4x50-freestyle', label: '4×50m Livre', distance: 200, style: 'freestyle', relay_style: 'freestyle', legs: 4, leg_distance_m: 50 },
  { value: '4x100-freestyle', label: '4×100m Livre', distance: 400, style: 'freestyle', relay_style: 'freestyle', legs: 4, leg_distance_m: 100 },
  { value: '4x200-freestyle', label: '4×200m Livre', distance: 800, style: 'freestyle', relay_style: 'freestyle', legs: 4, leg_distance_m: 200 },
  { value: '4x50-medley', label: '4×50m Medley', distance: 200, style: 'medley', relay_style: 'medley', legs: 4, leg_distance_m: 50 },
  { value: '4x100-medley', label: '4×100m Medley', distance: 400, style: 'medley', relay_style: 'medley', legs: 4, leg_distance_m: 100 },
];

// Combined (backward compatible — used for lookup)
const EVENTS = [...INDIVIDUAL_EVENTS, ...RELAY_EVENTS];

const GENDERS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

const PHASES = [
  { value: 'Final', label: 'Final' },
  { value: 'Semifinal', label: 'Semifinal' },
  { value: 'Eliminatórias', label: 'Eliminatórias' },
  { value: 'Final B', label: 'Final B' },
];

const STATUS_CONFIG = {
  pending:    { label: 'Na fila',      color: VUKZ.textLight, icon: Clock },
  queued:     { label: 'Na fila',      color: VUKZ.textLight, icon: Clock },
  processing: { label: 'Processando', color: '#2563eb',      icon: Loader2, spin: true },
  done:       { label: 'Concluído',   color: VUKZ.success,   icon: CheckCircle2 },
  completed:  { label: 'Concluído',   color: VUKZ.success,   icon: CheckCircle2 },
  failed:     { label: 'Erro',        color: VUKZ.error,     icon: AlertTriangle },
  cancelled:  { label: 'Cancelado',   color: VUKZ.error,     icon: X },
};

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export default function BatchAnalysisPage() {
  // Phase: 'config' | 'uploading' | 'tracking'
  const [phase, setPhase] = useState('config');

  // Config phase state
  const [items, setItems] = useState([]); // [{ id, file, event, lane }]
  const [competition, setCompetition] = useState(''); // shared competition name
  const [gender, setGender] = useState(''); // shared gender M/F
  const [eventType, setEventType] = useState('individual'); // individual or relay
  const [competitions, setCompetitions] = useState([]);
  const fileInputRef = useRef(null);

  // Upload phase state
  const [uploadProgress, setUploadProgress] = useState({}); // { itemId: 0-100 }
  const [uploadErrors, setUploadErrors] = useState({});     // { itemId: 'error msg' }

  // Tracking phase state
  const [batchId, setBatchId] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const pollingRef = useRef(null);

  // ── Fetch competitions ────────────────────────────────────────

  useEffect(() => {
    const fetchComps = async () => {
      try {
        const { data } = await AZURE.get('/swimming/competitions', { params: { limit: 500 } });
        setCompetitions(data?.items || data?.data || []);
      } catch (err) {
        console.error('[Batch] Failed to fetch competitions:', err);
      }
    };
    fetchComps();
  }, []);

  // ── File selection ──────────────────────────────────────────────

  const addFiles = useCallback((files) => {
    const videoFiles = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return ACCEPTED_VIDEO_EXTENSIONS.includes(ext);
    });

    setItems(prev => {
      const remaining = MAX_VIDEOS - prev.length;
      const toAdd = videoFiles.slice(0, remaining).map(f => ({
        id: crypto.randomUUID(),
        file: f,
        event: '',
        fase: 'Final',
        lane: '',
        athlete_name: '',
      }));
      return [...prev, ...toAdd];
    });
  }, []);

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  // ── Drag & drop ────────────────────────────────────────────────

  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  // ── Validation ─────────────────────────────────────────────────

  const allConfigured = items.length > 0 && items.every(i => i.event && i.lane && i.fase) && competition && gender;

  // ── Upload + Progressive session creation ──────────────────────
  // Each video uploads sequentially — as soon as one enters the queue,
  // the next starts uploading. This way the GPU starts processing video 1
  // while video 2 is still uploading.

  const startBatch = async () => {
    setPhase('uploading');
    setUploadProgress({});
    setUploadErrors({});

    const sessionIds = [];
    const uploadedVideos = [];

    // Sequential: upload + create session one by one
    for (const item of items) {
      try {
        // 1. Get SAS URL
        const sasResp = await getUploadSas(item.file.name);
        if (!sasResp?.success || !sasResp.data?.sas_url) {
          throw new Error('Failed to get upload URL');
        }

        // 2. Upload to blob
        await uploadFileToBlob(
          sasResp.data.sas_url,
          item.file,
          ({ loaded, total }) => {
            setUploadProgress(prev => ({
              ...prev,
              [item.id]: Math.round((loaded / total) * 100),
            }));
          }
        );

        setUploadProgress(prev => ({ ...prev, [item.id]: 100 }));

        // 3. Immediately create session (enters queue right away)
        const eventInfo = EVENTS.find(e => e.value === item.event);
        const isRelay = eventType === 'relay';
        const sessionPayload = {
          blob_url: sasResp.data.blob_url,
          filename: item.file.name,
          swimming_style: eventInfo?.style || 'freestyle',
          pool_length_m: 50,
          race_distance_m: eventInfo?.distance || 50,
          analysis_mode: 'competition',
          target_lanes: item.lane ? [parseInt(item.lane, 10)] : undefined,
          athlete_name: item.athlete_name || undefined,
          competition_name: competition || undefined,
          gender: gender || undefined,
          fase: item.fase || 'Final',
          event_type: eventType,
        };
        // Add relay-specific fields
        if (isRelay && eventInfo) {
          sessionPayload.relay_style = eventInfo.relay_style || eventInfo.style;
          sessionPayload.legs = eventInfo.legs || 4;
          sessionPayload.leg_distance_m = eventInfo.leg_distance_m || 100;
        }
        const { data: sessionResp } = await AZURE.post('/vukz/biomechanics/create-session',
          sessionPayload, { timeout: 30000 });

        const sid = sessionResp?.data?.session_id;
        if (sid) {
          sessionIds.push(sid);
          setUploadProgress(prev => ({ ...prev, [item.id]: 'queued' }));
          uploadedVideos.push({
            session_id: sid,
            filename: item.file.name,
            style: eventInfo?.style || 'freestyle',
            lane: item.lane ? parseInt(item.lane, 10) : undefined,
            athlete_name: item.athlete_name || undefined,
            race_distance_m: eventInfo?.distance || 50,
          });
        }
        // → Next video starts uploading immediately (GPU already processing this one)
      } catch (err) {
        console.error(`[Batch] Failed for ${item.file.name}:`, err);
        setUploadErrors(prev => ({ ...prev, [item.id]: err.message }));
        // Continue with next video even if one fails
      }
    }

    if (sessionIds.length === 0) {
      setPhase('config');
      return;
    }

    // Group sessions for tracking
    try {
      const resp = await createBatch({
        videos: uploadedVideos,
        competition_name: competition,
        gender: gender,
        race_distance_m: uploadedVideos[0]?.race_distance_m,
      });

      if (resp?.success && resp.data?.batch_id) {
        setBatchId(resp.data.batch_id);
        setBatchData(resp.data);
      } else {
        // Fallback: just track session IDs directly
        setBatchId(`local-${Date.now()}`);
        setBatchData({ total: sessionIds.length, done: 0, failed: 0, sessions: sessionIds.map(id => ({ session_id: id, status: 'queued' })) });
      }
      setPhase('tracking');
    } catch (err) {
      // Sessions are already processing — go to tracking with local state
      console.warn('[Batch] Batch grouping failed, tracking individually:', err.message);
      setBatchId(`local-${Date.now()}`);
      setBatchData({ total: sessionIds.length, done: 0, failed: 0, sessions: sessionIds.map(id => ({ session_id: id, status: 'queued' })) });
      setPhase('tracking');
    }
  };

  // ── Polling ────────────────────────────────────────────────────

  useEffect(() => {
    if (phase !== 'tracking' || !batchId) return;

    const poll = async () => {
      try {
        const resp = await getBatchStatus(batchId);
        if (resp?.success) {
          setBatchData(resp.data);
          // Stop polling when all done or failed
          const d = resp.data;
          if (d.done + d.failed >= d.total) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch (err) {
        console.warn('[Batch] Poll error:', err.message);
      }
    };

    poll(); // immediate first poll
    pollingRef.current = setInterval(poll, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [phase, batchId]);

  // ── Format helpers ─────────────────────────────────────────────

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const styleLabel = (val) => EVENTS.find(e => e.value === val)?.label || val || '—';

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-4xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
            <Layers size={20} color="white" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold" style={{ color: VUKZ.text }}>
              Análise em Lote
            </h1>
            <p className="text-sm" style={{ color: VUKZ.textLight }}>
              Envie até {MAX_VIDEOS} vídeos para análise simultânea
            </p>
          </div>
          <Link
            to="/swim-vision"
            className="ml-auto text-xs font-bold px-3 py-1.5 rounded-lg border transition-all hover:bg-gray-50"
            style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
          >
            ← Análise Individual
          </Link>
        </div>

        {/* ═══ PHASE 1: CONFIG ═══ */}
        {phase === 'config' && (
          <div className="space-y-4">

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all"
              style={{
                borderColor: dragOver ? VUKZ.primary : VUKZ.border,
                backgroundColor: dragOver ? `${VUKZ.primary}08` : 'white',
              }}
            >
              <Upload size={32} style={{ color: VUKZ.textLight }} className="mx-auto mb-3" />
              <p className="text-sm font-bold" style={{ color: VUKZ.text }}>
                Arraste vídeos aqui ou clique para selecionar
              </p>
              <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>
                MP4, MOV, AVI, WebM — máx. {MAX_VIDEOS} vídeos
              </p>
              {items.length > 0 && (
                <p className="text-xs mt-2 font-bold" style={{ color: VUKZ.primary }}>
                  {items.length}/{MAX_VIDEOS} selecionados
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_VIDEO_EXTENSIONS.join(',')}
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {/* Batch shared fields: competition + gender */}
            {items.length > 0 && (
              <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: VUKZ.border }}>
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>
                  Dados do Lote
                </p>

                {/* Individual / Revezamento toggle */}
                <div>
                  <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: VUKZ.textLight }}>
                    Tipo de Prova
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'individual', label: 'Individual' },
                      { value: 'relay', label: 'Revezamento' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setEventType(opt.value);
                          // Clear selected events when switching type
                          setItems(prev => prev.map(i => ({ ...i, event: '' })));
                        }}
                        className="px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all"
                        style={{
                          borderColor: eventType === opt.value ? VUKZ.primary : VUKZ.border,
                          backgroundColor: eventType === opt.value ? VUKZ.primary : 'white',
                          color: eventType === opt.value ? 'white' : VUKZ.text,
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: VUKZ.textLight }}>
                      Competição
                    </label>
                    <input
                      type="text"
                      list="competitions-list"
                      value={competition}
                      onChange={(e) => setCompetition(e.target.value)}
                      placeholder="Digite ou selecione a competição..."
                      className="w-full text-xs font-bold px-3 py-2 rounded-lg border bg-white"
                      style={{
                        borderColor: competition ? VUKZ.primary : VUKZ.error,
                        color: competition ? VUKZ.text : VUKZ.error,
                      }}
                    />
                    <datalist id="competitions-list">
                      {competitions.map(c => (
                        <option key={c.id} value={c.name}>{c.name} ({c.year})</option>
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: VUKZ.textLight }}>
                      Sexo
                    </label>
                    <select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className="w-full text-xs font-bold px-3 py-2 rounded-lg border appearance-none bg-white"
                      style={{
                        borderColor: gender ? VUKZ.primary : VUKZ.error,
                        color: gender ? VUKZ.text : VUKZ.error,
                      }}
                    >
                      <option value="">Sexo...</option>
                      {GENDERS.map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Video list */}
            {items.length > 0 && (
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border p-3 flex items-center gap-2"
                    style={{ borderColor: VUKZ.border }}
                  >
                    {/* Index */}
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black flex-shrink-0"
                      style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}
                    >
                      {idx + 1}
                    </div>

                    {/* Filename */}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold truncate" style={{ color: VUKZ.text }}>
                        {item.file.name}
                      </p>
                      <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                        {formatSize(item.file.size)}
                      </p>
                    </div>

                    {/* Event (prova) selector */}
                    <select
                      value={item.event}
                      onChange={(e) => updateItem(item.id, 'event', e.target.value)}
                      className="text-[11px] font-bold px-2 py-1.5 rounded-lg border appearance-none bg-white"
                      style={{
                        borderColor: item.event ? VUKZ.primary : VUKZ.error,
                        color: item.event ? VUKZ.text : VUKZ.error,
                        minWidth: 120,
                      }}
                    >
                      <option value="">Prova...</option>
                      {(eventType === 'relay' ? RELAY_EVENTS : INDIVIDUAL_EVENTS).map(e => (
                        <option key={e.value} value={e.value}>{e.label}</option>
                      ))}
                    </select>

                    {/* Fase (phase) selector per item */}
                    <select
                      value={item.fase || 'Final'}
                      onChange={(e) => updateItem(item.id, 'fase', e.target.value)}
                      className="text-[11px] font-bold px-2 py-1.5 rounded-lg border appearance-none bg-white"
                      style={{
                        borderColor: VUKZ.primary,
                        color: VUKZ.text,
                        minWidth: 80,
                      }}
                    >
                      {PHASES.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>

                    {/* Lane + Athlete inline group */}
                    <div className="flex items-center gap-1 border rounded-lg px-1.5 py-0.5" style={{ borderColor: item.lane ? VUKZ.border : VUKZ.error }}>
                      <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>R</span>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        placeholder="—"
                        value={item.lane}
                        onChange={(e) => updateItem(item.id, 'lane', e.target.value)}
                        className="text-xs font-bold text-center border-0 outline-none bg-transparent"
                        style={{ color: VUKZ.text, width: 28 }}
                      />
                      <div className="w-px h-4 bg-gray-200" />
                      <input
                        type="text"
                        placeholder="Atleta"
                        value={item.athlete_name}
                        onChange={(e) => updateItem(item.id, 'athlete_name', e.target.value)}
                        className="text-[11px] px-1 py-0 border-0 outline-none bg-transparent"
                        style={{ color: VUKZ.text, width: 90 }}
                      />
                    </div>

                    {/* Duplicate (same video, different lane) */}
                    {items.length < MAX_VIDEOS && (
                      <button
                        onClick={() => setItems(prev => [...prev, { id: crypto.randomUUID(), file: item.file, event: item.event, fase: item.fase || 'Final', lane: '', athlete_name: '' }])}
                        className="p-1 rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0"
                        title="Duplicar (outra raia)"
                      >
                        <Layers size={13} style={{ color: '#2563eb' }} />
                      </button>
                    )}

                    {/* Remove */}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-1 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <Trash2 size={13} style={{ color: VUKZ.error }} />
                    </button>
                  </div>
                ))}

                {/* Add more button */}
                {items.length < MAX_VIDEOS && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-2.5 rounded-xl border-2 border-dashed text-xs font-bold transition-all hover:bg-gray-50"
                    style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
                  >
                    <Plus size={14} className="inline mr-1" /> Adicionar mais vídeos
                  </button>
                )}
              </div>
            )}

            {/* Start button */}
            {items.length > 0 && (
              <button
                onClick={startBatch}
                disabled={!allConfigured}
                className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: VUKZ_GRADIENT, color: 'white' }}
              >
                <Waves size={16} className="inline mr-2" />
                Analisar Lote ({items.length} vídeo{items.length > 1 ? 's' : ''})
              </button>
            )}

            {!allConfigured && items.length > 0 && (
              <p className="text-xs text-center" style={{ color: VUKZ.error }}>
                Preencha competição, sexo, prova e raia de cada vídeo para continuar
              </p>
            )}
          </div>
        )}

        {/* ═══ PHASE 2: UPLOADING ═══ */}
        {phase === 'uploading' && (
          <div className="space-y-3">
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: VUKZ.border }}>
              <p className="text-sm font-bold mb-3" style={{ color: VUKZ.text }}>
                <Loader2 size={14} className="inline mr-2 animate-spin" />
                Enviando {items.length} vídeo{items.length > 1 ? 's' : ''}...
              </p>
              {items.map((item, idx) => {
                const pct = uploadProgress[item.id] || 0;
                const err = uploadErrors[item.id];
                return (
                  <div key={item.id} className="flex items-center gap-3 py-2">
                    <span className="text-xs font-bold w-5 text-center" style={{ color: VUKZ.textLight }}>
                      {idx + 1}
                    </span>
                    <span className="text-xs font-bold flex-1 truncate" style={{ color: VUKZ.text }}>
                      {item.file.name}
                    </span>
                    {err ? (
                      <span className="text-[10px] font-bold" style={{ color: VUKZ.error }}>
                        Erro
                      </span>
                    ) : (
                      <div className="w-32 flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: `${pct}%`, backgroundColor: VUKZ.primary }}
                          />
                        </div>
                        <span className="text-[10px] font-bold w-8 text-right" style={{ color: VUKZ.textLight }}>
                          {pct}%
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {uploadErrors._batch && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-red-700">
                <AlertTriangle size={12} className="inline mr-1" />
                {uploadErrors._batch}
              </div>
            )}
          </div>
        )}

        {/* ═══ PHASE 3: TRACKING ═══ */}
        {phase === 'tracking' && batchData && (
          <div className="space-y-4">

            {/* Summary bar */}
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: VUKZ.border }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold" style={{ color: VUKZ.text }}>
                  Lote: {batchData.total} vídeo{batchData.total > 1 ? 's' : ''}
                </p>
                {batchData.done + batchData.failed < batchData.total ? (
                  <span className="flex items-center gap-1 text-xs font-bold" style={{ color: '#2563eb' }}>
                    <Loader2 size={12} className="animate-spin" /> Processando...
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-bold" style={{ color: VUKZ.success }}>
                    <CheckCircle2 size={12} /> Concluído
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div className="h-3 rounded-full overflow-hidden flex" style={{ backgroundColor: VUKZ.primaryLight }}>
                {batchData.done > 0 && (
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${(batchData.done / batchData.total) * 100}%`, backgroundColor: VUKZ.success }}
                  />
                )}
                {batchData.processing > 0 && (
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${(batchData.processing / batchData.total) * 100}%`, backgroundColor: '#2563eb' }}
                  />
                )}
                {batchData.failed > 0 && (
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${(batchData.failed / batchData.total) * 100}%`, backgroundColor: VUKZ.error }}
                  />
                )}
              </div>

              <div className="flex gap-4 mt-2">
                {[
                  { label: 'Concluídos', val: batchData.done, color: VUKZ.success },
                  { label: 'Processando', val: batchData.processing, color: '#2563eb' },
                  { label: 'Na fila', val: batchData.queued, color: VUKZ.textLight },
                  { label: 'Erro', val: batchData.failed, color: VUKZ.error },
                ].filter(x => x.val > 0).map(x => (
                  <span key={x.label} className="text-[10px] font-bold" style={{ color: x.color }}>
                    {x.val} {x.label.toLowerCase()}
                  </span>
                ))}
              </div>
            </div>

            {/* Session list */}
            <div className="space-y-2">
              {(batchData.sessions || []).map((s, idx) => {
                const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.pending;
                const Icon = cfg.icon;
                return (
                  <div
                    key={s.session_id}
                    className="bg-white rounded-xl border p-3 flex items-center gap-3"
                    style={{ borderColor: VUKZ.border }}
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}
                    >
                      <Icon size={14} className={cfg.spin ? 'animate-spin' : ''} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: VUKZ.text }}>
                        {s.filename}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }}>
                          {styleLabel(s.style)}
                        </span>
                        {s.lane && (
                          <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>
                            Raia {s.lane}
                          </span>
                        )}
                        {s.stroke_count != null && (
                          <span className="text-[10px] font-bold" style={{ color: VUKZ.primary }}>
                            {s.stroke_count} braçadas
                          </span>
                        )}
                      </div>
                      {s.error_message && (
                        <p className="text-[10px] mt-0.5" style={{ color: VUKZ.error }}>
                          {s.error_message}
                        </p>
                      )}
                    </div>

                    <span className="text-[10px] font-bold whitespace-nowrap" style={{ color: cfg.color }}>
                      {cfg.label}
                    </span>

                    {(s.status === 'done' || s.status === 'completed') && (
                      <Link
                        to={`/swim-vision/result/${s.session_id}`}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:opacity-80 flex-shrink-0"
                        style={{ backgroundColor: VUKZ.primary, color: 'white' }}
                      >
                        <Eye size={12} className="inline mr-1" /> Ver
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            {/* New batch button */}
            {batchData.done + batchData.failed >= batchData.total && (
              <button
                onClick={() => {
                  setPhase('config');
                  setItems([]);
                  setBatchId(null);
                  setBatchData(null);
                }}
                className="w-full py-3 rounded-xl text-sm font-bold border-2 transition-all hover:bg-gray-50"
                style={{ borderColor: VUKZ.border, color: VUKZ.text }}
              >
                <Plus size={16} className="inline mr-2" /> Novo Lote
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
